import React from 'react';
import {
  View,
  Text,
  StyleSheet,
} from 'react-native';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { useAppSelector } from '@/store';
import { PaymentDetailsSummary } from './shared';

interface TransactionReceivedContentProps {
  paymentAmount: number;
  fee: number;
  totalCharged: number;
  paymentMethodLabel: string;
  residentName?: string;
  // Optional processor reference fields — shown if present so support can
  // correlate the charge back to CardConnect/Stitch and the Resio DB row.
  paymentId?: number;
  retref?: string;
  authcode?: string;
}

/**
 * Transaction Received Content - Shows success message and payment details
 */
export const TransactionReceivedContent: React.FC<TransactionReceivedContentProps> = ({
  paymentAmount,
  fee,
  totalCharged,
  paymentMethodLabel,
  residentName,
  paymentId,
  retref,
  authcode,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  const userEmail = authUser?.email || 'your email';

  return (
    <View style={styles.container}>
      {/* Thank You */}
      <Text style={[styles.thankYouTitle, { color: MARKETPLACE_COLORS.textPrimary }]}>Thank you!</Text>

      {/* Confirmation Message */}
      <Text style={[styles.confirmationText, { color: MARKETPLACE_COLORS.textPrimary }]}>
        Your transaction has been received. A confirmation has been sent to {userEmail}.
      </Text>

      {/* Payment Details */}
      <PaymentDetailsSummary
        paymentAmount={paymentAmount}
        fee={fee}
        totalAmount={totalCharged}
        paymentMethodLabel={paymentMethodLabel}
        residentName={residentName}
        containerStyle={styles.detailsContainer}
      />

      {(retref || authcode || paymentId != null) && (
        <View style={styles.referenceBlock}>
          {retref && (
            <View style={styles.referenceRow}>
              <Text style={[styles.referenceLabel, { color: MARKETPLACE_COLORS.textMuted }]}>Reference #</Text>
              <Text style={[styles.referenceValue, { color: MARKETPLACE_COLORS.textMuted }]}>{retref}</Text>
            </View>
          )}
          {authcode && (
            <View style={styles.referenceRow}>
              <Text style={[styles.referenceLabel, { color: MARKETPLACE_COLORS.textMuted }]}>Authorization</Text>
              <Text style={[styles.referenceValue, { color: MARKETPLACE_COLORS.textMuted }]}>{authcode}</Text>
            </View>
          )}
          {paymentId != null && (
            <View style={styles.referenceRow}>
              <Text style={[styles.referenceLabel, { color: MARKETPLACE_COLORS.textMuted }]}>Payment ID</Text>
              <Text style={[styles.referenceValue, { color: MARKETPLACE_COLORS.textMuted }]}>{paymentId}</Text>
            </View>
          )}
        </View>
      )}

      {/* Processing Note */}
      <Text style={[styles.processingNote, { color: MARKETPLACE_COLORS.textMuted }]}>
        Payments can take up to 5 business days to process completely. Please be sure to keep sufficient funds in your account.
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  thankYouTitle: {
    fontSize: 18,
    fontWeight: '600',
    lineHeight: 27,
    marginBottom: 16,
  },
  confirmationText: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
    marginBottom: 24,
  },
  detailsContainer: {
    marginBottom: 24,
  },
  referenceBlock: {
    marginBottom: 24,
    gap: 4,
  },
  referenceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 2,
  },
  referenceLabel: {
    fontSize: 13,
    fontWeight: '400',
    lineHeight: 20,
  },
  referenceValue: {
    fontSize: 13,
    fontWeight: '500',
    lineHeight: 20,
  },
  processingNote: {
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
  },
});

export default TransactionReceivedContent;
